import type { ThreadMessageLike } from "@assistant-ui/react";
import {
  normalizeSafetyExitMetadata,
  safetyExitMetadataFromAssistantText,
  withSafetyExitMetadata,
} from "../safetyExit";
import { normalizeFollowUpSuggestions } from "./socketConnection";
import { buildAssistantContentWithSources } from "./sourceContent";
import type { SessionTurn } from "./types";

export function resumeTurnsToThreadMessages(turns: readonly SessionTurn[] = []): ThreadMessageLike[] {
  return turns.flatMap((turn) => {
    const createdAt = persistedTurnDate(turn.created_at);
    const explicitSafetyExit = normalizeSafetyExitMetadata(
      turn.agent_metadata?.safetyExit ?? turn.agent_metadata?.safety_exit,
    );
    const safetyExit = explicitSafetyExit ?? safetyExitMetadataFromAssistantText(turn.assistant_response);
    const followUpSuggestions = normalizeFollowUpSuggestions(turn.follow_up_suggestions);
    const assistantContent = buildAssistantContentWithSources(turn.assistant_response, {
      agentMetadata: turn.agent_metadata,
      citations: turn.citations,
    });
    // The explicit safetyExit spread handles stale canned safety responses; the
    // wrapper below is a normalization-only pass because user-text fallback is disabled.
    return [
      {
        id: `${turn.trace_id}-user`,
        role: "user" as const,
        content: [{ type: "text" as const, text: turn.user_message }],
        createdAt,
        metadata: {
          custom: {
            guestId: turn.guest_id,
            threadId: turn.thread_id,
            memoryId: turn.memory_id,
            traceId: turn.trace_id,
            hydratedFrom: "session.resume",
            ...(turn.client_message_id ? { clientMessageId: turn.client_message_id } : {}),
          },
        },
      },
      {
        id: `${turn.trace_id}-assistant`,
        role: "assistant" as const,
        content: assistantContent,
        createdAt,
        metadata: withSafetyExitMetadata({
          custom: {
            guestId: turn.guest_id,
            threadId: turn.thread_id,
            memoryId: turn.memory_id,
            traceId: turn.trace_id,
            hydratedFrom: "session.resume",
            ...(turn.agent_metadata ? { agentMetadata: turn.agent_metadata } : {}),
            ...(followUpSuggestions.length > 0 ? { followUpSuggestions } : {}),
            ...(turn.client_message_id ? { clientMessageId: turn.client_message_id } : {}),
            ...(safetyExit ? { safetyExit } : {}),
          },
        }, turn.user_message, { allowLocalFallback: false }),
      },
    ];
  });
}

function persistedTurnDate(value: string | null | undefined): Date {
  if (value) {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) return date;
  }
  return new Date();
}
